import type { AdamCommand, AdamCommandContext, AdamCommandResult, AdamSelectionState } from "./Command";
import { AdamCommandHistory } from "./CommandHistory";

export type AdamCommandHostOptions = {
  /** Build a live adapter context for execute/undo/redo. */
  createContext: () => AdamCommandContext;
  /** After a document-affecting command, flush UI / clip / engine as needed. */
  commit: (result: AdamCommandResult, selection: AdamSelectionState | undefined) => void;
  maxDepth?: number;
};

/**
 * Thin host: history.execute → commit. PaintController (and later labRuntime)
 * owns createContext / commit against live state.
 */
export class AdamCommandHost {
  readonly history: AdamCommandHistory;

  constructor(private readonly options: AdamCommandHostOptions) {
    this.history = new AdamCommandHistory(options.maxDepth);
  }

  execute(
    command: AdamCommand,
    options: {
      beforeSelection?: AdamSelectionState;
      afterSelection?: AdamSelectionState;
      /**
       * When `afterSelection` is omitted, sample selection again after execute
       * (project ops like addTrack may jump the cursor during execute).
       */
      captureAfterSelection?: boolean;
    } = {},
  ): AdamCommandResult {
    const context = this.options.createContext();
    const beforeSelection = options.beforeSelection ?? context.selection;
    const captureAfter =
      options.afterSelection === undefined && options.captureAfterSelection !== false;
    const result = this.history.execute(command, context, {
      beforeSelection,
      afterSelection: options.afterSelection ?? beforeSelection,
    });
    if (captureAfter) {
      const afterSelection = this.options.createContext().selection;
      context.selection = afterSelection;
      this.history.setLastAfterSelection(afterSelection);
    }
    if (command.affectsDocument !== false) {
      this.options.commit(result, context.selection);
    }
    return result;
  }

  undo(): AdamCommandResult | null {
    const context = this.options.createContext();
    const result = this.history.undo(context);
    if (!result) {
      return null;
    }
    this.options.commit(result, context.selection);
    return result;
  }

  redo(): AdamCommandResult | null {
    const context = this.options.createContext();
    const result = this.history.redo(context);
    if (!result) {
      return null;
    }
    this.options.commit(result, context.selection);
    return result;
  }

  clear(): void {
    this.history.clear();
  }

  getState() {
    return this.history.getState();
  }
}
